import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Reveal, MaskedLines, FadeIn } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const HERO_IMG =
  "https://images.pexels.com/photos/17108143/pexels-photo-17108143.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CABINET_IMG =
  "https://images.unsplash.com/photo-1670003112050-710fbf757482?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxnZW5ldmElMjBhcmNoaXRlY3R1cmUlMjBsdXh1cnl8ZW58MHx8fHwxNzkyMjc1NjI5fDA&ixlib=rb-4.1.0&q=85";
const MEETING_IMG =
  "https://images.pexels.com/photos/8428082/pexels-photo-8428082.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Hero = () => {
  const { t } = useTranslation("home");
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-32 lg:pt-40">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 right-[-10%] h-[520px] w-[520px] rounded-full opacity-60"
        style={{ background: "radial-gradient(circle, rgba(197,168,128,0.10) 0%, transparent 65%)" }}
      />
      <div className="mx-auto grid max-w-7xl gap-14 px-6 pb-20 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:px-12 lg:pb-28">
        <div className="lg:col-span-7">
          <FadeIn delay={0.1}>
            <p className="overline-label mb-8 flex items-center gap-3" data-testid="hero-overline">
              <span className="inline-block h-px w-10 bg-brass/60" />
              {t("hero.overline")}
            </p>
          </FadeIn>

          <h1
            data-testid="hero-heading"
            className="font-display text-4xl font-light leading-[1.08] tracking-tight text-chalk sm:text-5xl lg:text-6xl"
          >
            <MaskedLines
              delay={0.2}
              lines={[
                t("hero.titleA"),
                <span key="b" className="font-serif italic text-brass">{t("hero.titleB")}</span>,
              ]}
            />
          </h1>

          <FadeIn delay={0.7}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-mist/85">{t("hero.lead")}</p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/contact" data-testid="hero-cta-contact-button" className="btn-brass">
                {t("hero.ctaPrimary")} <ArrowUpRight size={15} />
              </Link>
              <Link to="/#methode" data-testid="hero-cta-method-button" className="btn-ghost">
                {t("hero.ctaSecondary")}
              </Link>
            </div>
            <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.24em] text-slate2/70">
              {t("hero.coords")}
            </p>
          </FadeIn>
        </div>

        <div className="relative lg:col-span-5">
          <FadeIn delay={0.5} className="relative">
            <div className="relative overflow-hidden border border-white/10">
              <motion.img
                src={HERO_IMG}
                alt={t("hero.imgAlt")}
                className="h-[340px] w-full object-cover opacity-80 lg:h-[480px]"
                style={{ y: imgY, scale: 1.12 }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-5 py-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-chalk/80">
                  {t("hero.imgLeft")}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brass">
                  {t("hero.imgRight")}
                </span>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-4 hidden border border-brass/30 bg-ink/90 px-6 py-4 backdrop-blur-sm sm:block lg:-left-10">
              <p className="font-display text-3xl text-chalk" data-testid="hero-stat-experience">25<span className="text-brass">+</span></p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate2">
                {t("hero.badgeLabel")}
              </p>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

const StatsBand = () => {
  const { t } = useTranslation("home");
  const stats = t("stats", { returnObjects: true });
  return (
    <section className="border-t border-white/[0.06]">
      <div className="mx-auto grid max-w-7xl grid-cols-2 px-6 sm:px-8 lg:grid-cols-4 lg:px-12">
        {stats.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.08} className={`border-white/[0.06] px-2 py-10 lg:px-8 ${i > 0 ? "border-l max-lg:[&:nth-child(3)]:border-l-0" : ""} max-lg:[&:nth-child(n+3)]:border-t`}>
            <p data-testid={`hero-stat-${s.id}`} className="font-display text-3xl text-chalk lg:text-4xl">
              {s.suffix && <span className="mr-1 font-mono text-xs uppercase tracking-[0.2em] text-brass">{s.suffix}</span>}
              {s.value}
            </p>
            <p className="mt-2 text-sm text-mist/80">{s.label}</p>
            <p className="mt-1 text-xs leading-relaxed text-slate2/70">{s.detail}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const ExpertisePreview = () => {
  const { t } = useTranslation("home");
  const items = t("expertises.items", { returnObjects: true });
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
      <div className="grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="overline-label">{t("expertises.overline")}</p>
            <h2 className="mt-4 font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl lg:text-4xl">
              {t("expertises.titleA")}
              <br />
              <span className="font-serif italic text-brass">{t("expertises.titleB")}</span>
            </h2>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-slate2">{t("expertises.lead")}</p>
            <Link to="/services" data-testid="expertises-see-all-link" className="btn-ghost mt-8">
              {t("expertises.link")} <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>

        <div className="lg:col-span-8">
          {items.map((e, i) => (
            <Reveal key={e.id} delay={i * 0.06}>
              <Link
                to="/contact"
                data-testid={`expertise-card-${e.id}`}
                className="group grid grid-cols-[auto_1fr_auto] items-baseline gap-6 border-t border-white/[0.08] py-7 transition-colors duration-300 last:border-b hover:bg-white/[0.02] lg:gap-10 lg:px-4"
              >
                <span className="font-mono text-xs tracking-[0.2em] text-brass/70">{e.num}</span>
                <span>
                  <span className="block font-display text-lg text-chalk transition-colors duration-300 group-hover:text-brass sm:text-xl">
                    {e.title}
                  </span>
                  <span className="mt-2 block max-w-xl text-sm leading-relaxed text-slate2">
                    {e.tagline}
                  </span>
                </span>
                <ArrowUpRight
                  size={18}
                  className="translate-y-0 text-slate2 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-brass"
                />
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const MethodPreview = () => {
  const { t } = useTranslation("home");
  const steps = t("method.steps", { returnObjects: true });
  return (
    <section id="methode" className="scroll-mt-24 border-y border-white/[0.06] bg-surface/40">
      <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
        <Reveal>
          <p className="overline-label">{t("method.overline")}</p>
          <h2 className="mt-4 max-w-2xl font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl lg:text-4xl">
            {t("method.titleA")}
            <span className="font-serif italic text-brass">{t("method.titleB")}</span>
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-px bg-white/[0.06] sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((m, i) => (
            <Reveal key={m.num} delay={i * 0.08} className="bg-ink">
              <div data-testid={`methodology-step-${i + 1}`} className="group h-full p-8 transition-colors duration-300 hover:bg-elevated">
                <p className="font-mono text-xs tracking-[0.24em] text-brass/70">{m.num}</p>
                <h3 className="mt-5 font-display text-lg text-chalk">{m.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-slate2">{m.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const CabinetTeaser = () => {
  const { t } = useTranslation("home");
  const facts = t("cabinet.facts", { returnObjects: true });
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
      <div className="grid items-center gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="grid grid-cols-2 gap-4">
              <div className="overflow-hidden border border-white/10">
                <img
                  src={CABINET_IMG}
                  alt={t("cabinet.imgAlt1")}
                  className="h-64 w-full object-cover transition-transform duration-700 hover:scale-105 lg:h-80"
                />
              </div>
              <div className="mt-10 overflow-hidden border border-white/10">
                <img
                  src={MEETING_IMG}
                  alt={t("cabinet.imgAlt2")}
                  className="h-64 w-full object-cover transition-transform duration-700 hover:scale-105 lg:h-80"
                />
              </div>
            </div>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate2/70">
              {t("cabinet.caption")}
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-7 lg:pl-8">
          <Reveal>
            <p className="overline-label">{t("cabinet.overline")}</p>
            <h2 className="mt-4 font-display text-2xl font-normal tracking-tight text-chalk sm:text-3xl">
              {t("cabinet.titleA")}
              <span className="font-serif italic text-brass">{t("cabinet.titleB")}</span>
            </h2>
            <blockquote className="mt-6 font-serif text-xl font-light italic leading-relaxed text-chalk sm:text-2xl">
              {t("cabinet.quote")}
            </blockquote>
            <ul className="mt-10 space-y-3 border-t border-white/[0.08] pt-8 text-sm text-mist/80">
              {facts.map((f) => (
                <li key={f.label} className="flex justify-between gap-6">
                  <span className="text-slate2">{f.label}</span>
                  <span className="text-right font-mono text-xs text-chalk">{f.value}</span>
                </li>
              ))}
            </ul>
            <Link to="/le-cabinet" data-testid="cabinet-discover-link" className="btn-ghost mt-10">
              {t("cabinet.link")} <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Testimonials = () => {
  const { t } = useTranslation("home");
  const items = t("testimonials.items", { returnObjects: true });
  return (
    <section className="border-t border-white/[0.06] bg-surface/30">
      <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
        <Reveal>
          <p className="overline-label">{t("testimonials.overline")}</p>
          <h2 className="mt-4 font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
            {t("testimonials.titleA")}
            <span className="font-serif italic text-brass">{t("testimonials.titleB")}</span>
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-8 lg:grid-cols-3">
          {items.map((tst, i) => (
            <Reveal key={tst.author} delay={i * 0.1} className={i === 1 ? "lg:translate-y-8" : ""}>
              <figure
                data-testid={`testimonial-card-${i + 1}`}
                className="flex h-full flex-col border border-white/[0.08] bg-ink p-8 transition-colors duration-300 hover:border-brass/35"
              >
                <span className="font-display text-5xl leading-none text-brass/40">"</span>
                <blockquote className="mt-2 flex-1 font-serif text-base italic leading-relaxed text-mist">
                  {tst.quote}
                </blockquote>
                <figcaption className="mt-8 border-t border-white/[0.08] pt-5">
                  <p className="font-display text-sm text-chalk">{tst.author}</p>
                  <p className="mt-1 text-xs leading-relaxed text-slate2">{tst.role}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Faq = () => {
  const { t } = useTranslation("home");
  const items = t("faq.items", { returnObjects: true });
  return (
    <section id="faq" className="mx-auto max-w-7xl scroll-mt-24 px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="overline-label">{t("faq.overline")}</p>
            <h2 className="mt-4 font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
              {t("faq.titleA")}
              <span className="font-serif italic text-brass">{t("faq.titleB")}</span>
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-8">
          <Reveal>
            <Accordion type="single" collapsible data-testid="faq-accordion" className="w-full">
              {items.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-white/[0.08]">
                  <AccordionTrigger
                    data-testid={`faq-question-${i + 1}`}
                    className="py-6 text-left font-display text-base text-chalk hover:text-brass hover:no-underline sm:text-lg"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent
                    data-testid={`faq-answer-${i + 1}`}
                    className="pb-6 text-sm leading-relaxed text-slate2"
                  >
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const FinalCta = () => {
  const { t } = useTranslation("home");
  return (
    <section className="border-t border-white/[0.06] bg-surface/40">
      <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-28">
        <Reveal className="max-w-3xl">
          <h2 className="mt-4 font-display text-3xl font-light leading-[1.15] tracking-tight text-chalk sm:text-4xl lg:text-5xl">
            {t("finalCta.titleA")}
            <span className="font-serif italic text-brass">{t("finalCta.titleB")}</span>
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-mist/85">{t("finalCta.lead")}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/contact" data-testid="final-cta-contact-button" className="btn-brass">
              {t("finalCta.btnPrimary")} <ArrowUpRight size={15} />
            </Link>
            <Link to="/services" data-testid="final-cta-services-button" className="btn-ghost">
              {t("finalCta.btnSecondary")}
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />
      <StatsBand />
      <ExpertisePreview />
      <MethodPreview />
      <CabinetTeaser />
      <Testimonials />
      <Faq />
      <FinalCta />
    </div>
  );
}
